import React from 'react';
import {
  Button, Tag, IconServer, IconSites,
} from 'gatorcomponents';
import * as Styles from './ProductItem.styles';
import useLocale from '@/hooks/useLocale';
import { useHistory } from 'react-router';
import { useResize } from '@/hooks/useResize';
import { locale } from '@/utils/locale';
import { DOMAIN_STATUS } from '@/pages/Domains/DomainsListHandler/DomainsListHandler.types';
import { usePropagationStatus } from '@/hooks/domains/usePropagationStatus';
import { PROPAGATION_STATUS } from '@/enums/domains/domains.enum';
import { HOSTING_STATUS, DOMAIN_DISABLE_MANAGE, HOSTING_DISABLE_MANAGE } from './ProductItem.enum';
import { formatDate } from '@/utils/Formatters/formatDate';
import { homeAnalytics } from '@/analytics/home';

const ProductItem = ({
  variant, product,
}) => {
  const { home: homeLocale, domains: domainsLocale } = useLocale();
  const history = useHistory();
  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 960;
  const statusLowercase = product.status.toLowerCase();
  const disableManageHosting = Object.values(HOSTING_DISABLE_MANAGE).includes(statusLowercase);
  const disableManageDomain = Object.values(DOMAIN_DISABLE_MANAGE).includes(statusLowercase);

  const disableManage = (variant) => {
    switch (variant) {
      case 'hosting':
        return disableManageHosting;
      case 'domain':
        return disableManageDomain;
      default:
        return false;
    }
  };

  const domainLocale = domainsLocale && domainsLocale.domainsListPage;

  const handleConfigureHosting = () => {
    homeAnalytics.contractedHostingsManageClick();
    const url = `${locale('routes.products')}${locale('routes.productdetail')}/${product.id}`;
    history.push(url);
  };

  const handleConfigureDomain = () => {
    homeAnalytics.contractedDomainsManageClick();
    const url = `${locale('routes.domains')}${locale('routes.domaindetail')}/${product.id}`;
    history.push(url);
  };

  const handleConfigure = () => {
    switch (variant) {
      case 'hosting':
        return handleConfigureHosting();
      case 'domain':
        return handleConfigureDomain();
      default:
        return null;
    }
  };

  const getIcon = () => {
    switch (variant) {
      case 'hosting':
        return (<IconServer color="lowDark" size="lg" testId="IconServer" />);
      case 'domain':
        return (<IconSites color="lowDark" size="lg" testId="IconSites" />);
      default:
        return null;
    }
  };

  const tagRulesHosting = {
    [HOSTING_STATUS.PENDING]: {
      label: homeLocale.ContractedProducts.hostingStatus && homeLocale.ContractedProducts.hostingStatus.pending,
      variant: 'attention',
    },
    [HOSTING_STATUS.ACTIVE]: {
      label: homeLocale.ContractedProducts.hostingStatus && homeLocale.ContractedProducts.hostingStatus.active,
      variant: 'active',
    },
    [HOSTING_STATUS.COMPLETED]: {
      label: homeLocale.ContractedProducts.hostingStatus && homeLocale.ContractedProducts.hostingStatus.completed,
      variant: 'active',
    },
    [HOSTING_STATUS.SUSPENDED]: {
      label: homeLocale.ContractedProducts.hostingStatus && homeLocale.ContractedProducts.hostingStatus.suspended,
      variant: 'pending',
    },
    [HOSTING_STATUS.TERMINATED]: {
      label: homeLocale.ContractedProducts.hostingStatus && homeLocale.ContractedProducts.hostingStatus.terminated,
      variant: 'cancel',
    },
    [HOSTING_STATUS.CANCELLED]: {
      label: homeLocale.ContractedProducts.hostingStatus && homeLocale.ContractedProducts.hostingStatus.cancelled,
      variant: 'cancel',
    },
    [HOSTING_STATUS.FRAUD]: {
      label: homeLocale.ContractedProducts.hostingStatus && homeLocale.ContractedProducts.hostingStatus.fraud,
      variant: 'pending',
    },
    [HOSTING_STATUS.BUILDING]: {
      label: homeLocale.ContractedProducts.hostingStatus && homeLocale.ContractedProducts.hostingStatus.building,
      variant: 'problem',
    },
    [HOSTING_STATUS.ACTIVATION_PENDING]: {
      label: homeLocale.ContractedProducts.hostingStatus && homeLocale.ContractedProducts.hostingStatus.activationPending,
      variant: 'problem',
    },
  };

  const tagRulesDomain = {
    [DOMAIN_STATUS.PENDING]: {
      label: domainLocale && domainLocale.statusProductDomain.pending,
      variant: 'attention',
    },
    [DOMAIN_STATUS.ACTIVE]: {
      label: domainLocale && domainLocale.statusProductDomain.active,
      variant: 'active',
    },
    [DOMAIN_STATUS.CANCELLED]: {
      label: domainLocale && domainLocale.statusProductDomain.cancelled,
      variant: 'cancel',
    },
    [DOMAIN_STATUS.PENDING_TRANSFER]: {
      label: domainLocale && domainLocale.statusProductDomain.pendingTransfer,
      variant: 'pending',
    },
    [DOMAIN_STATUS.REGISTER_ERROR]: {
      label: domainLocale && domainLocale.statusProductDomain.registerError,
      variant: 'problem',
    },
    [DOMAIN_STATUS.EXPIRED]: {
      label: domainLocale && domainLocale.statusProductDomain.expired,
      variant: 'problem',
    },
    [DOMAIN_STATUS.NEXT_DUE_DATE]: {
      label: domainLocale && domainLocale.statusProductDomain.nextDueDate,
      variant: 'pending',
    },
    [DOMAIN_STATUS.OVERDUE_DATE]: {
      label: domainLocale && domainLocale.statusProductDomain.overDueDate,
      variant: 'problem',
    },
  };

  const mountTag = ({ label, variant = 'active' }) => (
    <Tag label={label} rounded={false} variant={variant} />
  );

  const propagationStatus = usePropagationStatus(product.domain);

  const domainTld = `[.${product.tld}]`;
  const displayStatusTag = !(['nextduedate', 'overduedate'].includes(statusLowercase));
  const propagationStatusText = domainLocale && domainLocale.propagationAlerts(propagationStatus);
  const hasNoRestrictions = propagationStatus
    && (propagationStatus !== PROPAGATION_STATUS.NOT_CONTRACTED)
    && (propagationStatus !== PROPAGATION_STATUS.NOT_CONFIGURED)
    && (propagationStatus !== PROPAGATION_STATUS.NOT_PROPAGATED);
  const validPropagationStatus = !!propagationStatus && !!propagationStatusText && hasNoRestrictions;

  const maxLenghtSm = (width * 4) / 100;
  const maxLenghtMd = (width * 6) / 100;

  const moumtDomain = () => (
    <Styles.WrapperDomain>
      <Styles.DomainSld>
        {product.sld}
      </Styles.DomainSld>
      <Styles.DomainTld>
        {domainTld}
      </Styles.DomainTld>
    </Styles.WrapperDomain>
  );

  return (
    <Styles.CardItem data-testid={`product-item-${product.id}`}>
      {mobile && (
        <Styles.CardItemRow>
          {variant === 'hosting' && mountTag(tagRulesHosting[statusLowercase])}
          {variant === 'domain' && displayStatusTag && mountTag(tagRulesDomain[statusLowercase])}
          {variant === 'domain' && validPropagationStatus && mountTag({ label: propagationStatusText.labelTag, variant: 'pending' })}
        </Styles.CardItemRow>
      )}
      <Styles.CardItemRow>
        <Styles.CardItemCollumn>
          <Styles.WrapperIcon>
            {getIcon()}
          </Styles.WrapperIcon>
        </Styles.CardItemCollumn>

        <Styles.CardItemCollumn collumnDomain>
          <Styles.WrapperCardItemTitle>
            <Styles.CardItemTitle>
              {variant === 'domain' ? (
                <>
                  {mobile ? (
                    <>
                      {product.name.length > maxLenghtSm ? moumtDomain() : product.name}
                    </>
                  ) : (
                    <>
                      {product.name.length > maxLenghtMd ? moumtDomain() : product.name}
                    </>
                  )}
                </>
              ) : (
                <>
                  {product.name}
                </>
              )}
            </Styles.CardItemTitle>

            {!mobile && variant === 'hosting' && mountTag(tagRulesHosting[statusLowercase])}
            {!mobile && variant === 'domain' && displayStatusTag && mountTag(tagRulesDomain[statusLowercase])}
            {!mobile && variant === 'domain' && validPropagationStatus && mountTag({ label: propagationStatusText.labelTag, variant: 'pending' })}
          </Styles.WrapperCardItemTitle>
          <Styles.CardItemSubtitle>
            {homeLocale.ContractedProducts.expiresOn}
            {' '}
            {formatDate(product.due_date)}
          </Styles.CardItemSubtitle>
        </Styles.CardItemCollumn>

        <Styles.CardItemCollumn last>
          <Button
            label={homeLocale.ContractedProducts.buttonLabelManage}
            onClick={handleConfigure}
            disabled={disableManage(variant)}
            size="small"
            testId="button"
            title={homeLocale.ContractedProducts.buttonLabelManage}
            type="button"
            variant="secondary"
            version="v2"
            width="fit-content"
          />
        </Styles.CardItemCollumn>
      </Styles.CardItemRow>
    </Styles.CardItem>
  );
};

export default ProductItem;
